import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Typography } from '@mui/material';
import FormFooter from 'components/FormComponents/FormFooter';
import FormHeader from 'components/FormComponents/FormHeader';
import Modal from 'components/ModalV2';
import { APPLICATION as sandbox } from 'config/sandbox';
import { APPLICATION as sandboxDev } from 'config/sandbox-dev';
// import { APPLICATION as sandboxDevLocal } from 'config/sandbox-dev-local';
import { USER_ENVIRONMENTS } from 'constants';
import { IS_SANDBOX_ENV } from 'helper/constant';
import { clearAccessToken, delay } from 'helper/utility';
import { capitalize } from 'lodash';
import PropTypes from 'prop-types';

import { updateEnvironmentWarningAction } from 'redux/actions/apiCall';
import { logoutAction } from 'redux/actions/auth';
import { openToast } from 'redux/actions/toaster';
import { selectLoggedInUser } from 'redux/selectors/auth';
import { selectEnvironmentWarning } from 'redux/selectors/general';
import { useSandboxAutoLogin } from 'redux/slices/globalApi';

import styles from '../dashboard.module.css';

const { SANDBOX, SANDBOX_DEV, PRODUCTION } = USER_ENVIRONMENTS;
let environment = process.env.REACT_APP_BUILD_ENV;

const ChangeEnvironmentWarning = ({ targetEnv }) => {
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();

    // const isSandboxLocalTarget = targetEnv === SANDBOX_LOCAL;
    const isSandboxDevTarget = targetEnv === SANDBOX_DEV;

    let modalTitle = SANDBOX;
    let modalContent = `You're now moving from the ${environment} to the sandbox environment — real data and actions will not apply from here on.`;

    if (IS_SANDBOX_ENV) {
        modalTitle = PRODUCTION;
        modalContent =
            "You're now moving from the sandbox to the production environment — real data and actions will apply from here on.";
    }

    if (isSandboxDevTarget) {
        modalTitle = 'Sandbox Dev';
        modalContent = `You're now moving from the ${environment} to the sandbox dev environment — real data and actions will not apply from here on.`;
    }

    // if (isSandboxLocalTarget) {
    //     modalTitle = 'Sandbox Local';
    //     modalContent = `You're now moving from the ${environment} to the sandbox local environment — real data and actions will not apply from here on.`;
    // }

    const showEnvPopup = useSelector(selectEnvironmentWarning);

    const onCloseModal = () => dispatch(updateEnvironmentWarningAction(false));

    const [sandboxAutoLogin] = useSandboxAutoLogin();

    const loggedInUser = useSelector(selectLoggedInUser);

    const triggerMessage = ({ variant, message }) => dispatch(openToast({ variant, message }));

    function getSandboxBaseURL() {
        // if (isSandboxLocalTarget) return sandboxDevLocal.URL;
        if (isSandboxDevTarget) return sandboxDev.URL;
        return sandbox.URL;
    }

    const handleEnvConfirm = async () => {
        try {
            setLoading(true);
            // if (IS_SANDBOX_ENV && !isSandboxLocalTarget) {
            if (IS_SANDBOX_ENV) {
                // Clear state and localStorage BEFORE redirecting
                clearAccessToken();
                dispatch(logoutAction());
                onCloseModal();

                // Wait for Redux state cleanup to complete before redirecting to production
                await delay(1000);

                window.location.href =
                    environment === SANDBOX ? sandbox.LIVE_ENV_URL : sandboxDev.LIVE_ENV_URL;
                return;
            }

            let payload = { adminEmail: loggedInUser.email };

            const data = await sandboxAutoLogin(payload).unwrap();

            onCloseModal();

            window.location.href = `${getSandboxBaseURL()}/sandbox-login?token=${data?.token}`;
        } catch (error) {
            onCloseModal();
            triggerMessage({
                variant: 'error',
                message: error?.data?.errorMessage || 'Something went wrong. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={showEnvPopup} onClose={onCloseModal} className={styles.environment_modal}>
            <FormHeader
                title={`Switch to ${capitalize(modalTitle)}?`}
                handleClose={onCloseModal}
                hideDivider
            />
            <Typography className={styles.environment_modal_content}>{modalContent}</Typography>
            <FormFooter
                btnText={loading ? 'Switching' : 'Confirm'}
                formType="onClick"
                secondaryBtnText="Cancel"
                secondaryBtnHandler={onCloseModal}
                primaryBtnHandler={handleEnvConfirm}
                primaryBtnLoading={loading}
                hideDivider
            />
        </Modal>
    );
};

ChangeEnvironmentWarning.propTypes = {
    targetEnv: PropTypes.string,
};

export default ChangeEnvironmentWarning;
